import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { StageProgress, StepCard, SelectField } from '../components/form/FormStepUI';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { readMatchParams } from '../utils/matchParams';

/**
 * Entry point for the match funnel. Collects the three things that determine
 * which programs we can match against, then hands off to the questionnaire.
 */
export function MatchStartPage() {
  usePageMeta('Find My Match | Online College Guide', 'Compare accredited online degree programs. Get matched free by career goals, schedule, and budget.');
  const navigate = useNavigate();
  // Start certification here, at the top of the funnel, so the certificate
  // covers the whole interaction rather than only the final screen.
  useLeadTracking();
  // Honor pre-populated links so campaign traffic skips choices it already made.
  const prefill = readMatchParams();
  const [degreeInterest, setDegreeInterest] = useState(prefill.degreeInterest || '');
  const [areaOfStudy, setAreaOfStudy] = useState(prefill.areaOfStudy || '');
  const [specialization, setSpecialization] = useState(prefill.specialization || '');
  const [error, setError] = useState('');
  const programOptions = specializationOptions.filter(spec => spec.focusValue === areaOfStudy);
  const handleAreaChange = (value: string) => {
    setAreaOfStudy(value);
    // A program from the previous area would no longer be valid.
    setSpecialization('');
    setError('');
  };
  const handleContinue = () => {
    if (!degreeInterest) {
      setError('Please select the degree level you are interested in.');
      return;
    }
    if (!areaOfStudy) {
      setError('Please select an area of study.');
      return;
    }
    if (!specialization) {
      setError('Please select a program.');
      return;
    }
    setError('');
    navigate('/request-information/form', {
      state: {
        degreeInterest,
        areaOfStudy,
        specialization
      }
    });
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2088" data-mp="2hmdoy176prc1">
      <Header data-id="element-2089" data-mp="2hoqcquzmivx0" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-2090" data-mp="2hogigv7jq2li">
        <div className="max-w-3xl mx-auto" data-id="element-2091" data-mp="2cmr7tgo8jqxy">
          <StageProgress currentStep={1} data-id="element-2092" data-mp="2cpwvcrh7ukrk" />

          <div className="mt-2 mb-4" data-id="element-2093" data-mp="2rjs13om2rtyj">
            <button type="button" onClick={() => navigate('/')} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2094" data-mp="2riqp9hx8j3fr">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2095" data-mp="s7yztfs982g0" />
              Back
            </button>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-2096" data-mp="33uj2hgemmiwf">
            <StepCard title="What would you like to study?" subtitle="Tell us the program you have in mind and we'll match you with accredited online colleges that offer it." onContinue={handleContinue} ctaLabel="Continue" info="Matching is free, and we only work with accredited partner schools. You choose whether to talk with any of them." error={error} data-id="element-2097" data-mp="2z3aqcclwwjfk">
              <div className="space-y-5" data-id="element-2098" data-mp="2g7b4zknfwe4y">
                <SelectField id="degree-level" label="Degree Level" value={degreeInterest} onChange={v => {
                setDegreeInterest(v);
                setError('');
              }} options={degreeInterestOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2099" data-mp="9g1z7582muxk" />

                <SelectField id="area-of-study" label="Area of Study" value={areaOfStudy} onChange={handleAreaChange} options={focusOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2100" data-mp="1whh0did1cibe" />

                <SelectField id="program" label="Program" value={specialization} onChange={v => {
                setSpecialization(v);
                setError('');
              }} disabled={!areaOfStudy} placeholder={areaOfStudy ? '-- Please Select --' : 'Select an area of study first'} options={programOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2101" data-mp="1ht3yk4r9o58z" />
              </div>
            </StepCard>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-2102" data-mp="2wq6wptqe75pa" />
    </div>;
}